import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Custom hook for auto-logout after inactivity
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 15)
 * @param {function} onLogout - Callback function to execute on logout
 */
export const useInactivityTimer = (timeoutMinutes = 15, onLogout) => {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  // Convert minutes to milliseconds
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_MS = (timeoutMinutes - 2) * 60 * 1000; // Show warning 2 minutes before logout
  
  const logout = useCallback(() => {
    console.log('⏰ Auto-logout due to inactivity');
    
    // Clear timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    // Show logout toast
    toast.warning('You have been logged out due to inactivity');
    
    // Execute logout callback if provided
    if (onLogout) {
      onLogout();
    } else {
      // Default logout behavior
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login', { 
        state: { 
          message: 'You have been logged out due to inactivity.',
          type: 'warning'
        }
      });
    }
  }, [navigate, onLogout]);
  
  const showWarning = useCallback(() => {
    console.log('⚠️ Inactivity warning: 2 minutes until auto-logout');
    toast.warning('You will be logged out in 2 minutes due to inactivity', {
      duration: 10000, // Show for 10 seconds
      description: 'Move your mouse or press any key to stay logged in'
    });
  }, []);
  
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    // Set warning timer (2 minutes before logout)
    if (WARNING_MS > 0) {
      warningTimerRef.current = setTimeout(() => {
        showWarning();
      }, WARNING_MS);
    }
    
    // Set logout timer
    timerRef.current = setTimeout(() => {
      logout();
    }, TIMEOUT_MS);
  }, [TIMEOUT_MS, WARNING_MS, logout, showWarning]);
  
  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
    
    // Reset timer on any activity
    const handleActivity = () => {
      resetTimer();
    };
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });
    
    // Initialize timer
    resetTimer();
    
    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [resetTimer]);
  
  return { resetTimer };
};

export default useInactivityTimer;
